import { useCallback, useEffect, useRef, useState } from "react";
import { getSpeakerNotes } from "../services/speakerNotesOffice";
import { getErrorMessage, logSpeakerNotesError } from "../services/speakerNotesApi";

export interface UseActiveSlideNotesReturn {
  notes: string;
  loading: boolean;
  refresh: () => Promise<void>;
}

/** Reports an addHandlerAsync/removeHandlerAsync failure — never `console.*`. */
function reportHandlerFailure(reason: string, fallback: string) {
  return (result: Office.AsyncResult<void>): void => {
    if (result.status === Office.AsyncResultStatus.Failed) {
      logSpeakerNotesError(reason, result.error.message || fallback);
    }
  };
}

/**
 * Reads the active slide's speaker notes and keeps them in sync as the user
 * navigates the deck, mirroring the DocumentSelectionChanged wiring in
 * useWordDocument.ts (PowerPoint fires the same event on slide navigation).
 */
export function useActiveSlideNotes(): UseActiveSlideNotesReturn {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  // Guards against race conditions between overlapping refresh() calls
  // resolving out of order.
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const text = await getSpeakerNotes();
      if (requestId === requestIdRef.current) {
        setNotes(text);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    refresh().catch((error: unknown) =>
      logSpeakerNotesError(
        "speaker-notes-current-notes-refresh",
        getErrorMessage(error, "Failed to load current speaker notes"),
      ),
    );

    try {
      Office.context.document.addHandlerAsync(
        Office.EventType.DocumentSelectionChanged,
        refresh,
        reportHandlerFailure("speaker-notes-selection-handler-add", "addHandlerAsync failed"),
      );
    } catch (error) {
      // Thrown synchronously when DocumentSelectionChanged isn't supported in this host.
      logSpeakerNotesError(
        "speaker-notes-selection-handler-add",
        getErrorMessage(error, "DocumentSelectionChanged is not supported in this host"),
      );
    }

    return () => {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: refresh } as Office.RemoveHandlerOptions,
          reportHandlerFailure("speaker-notes-selection-handler-remove", "removeHandlerAsync failed"),
        );
      } catch (error) {
        logSpeakerNotesError(
          "speaker-notes-selection-handler-remove",
          getErrorMessage(error, "DocumentSelectionChanged handler removal failed"),
        );
      }
    };
  }, [refresh]);

  return { notes, loading, refresh };
}
